import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Empty, Modal, Typography, message } from 'antd';
import { programApiErrorMessage, programLinkingExplanation } from './fields';
import ProgramLinkingLineItemsTable from './ProgramLinkingLineItemsTable';
import {
	useGetLinkableLineItemsQuery,
	useSetProgramLineItemsMutation,
} from '../../redux/api/manufacturing';
import type {
	ProgramOrderLineItem,
	ProgramType,
} from '../../interfaces/CamProgramming.interface';

const { Text } = Typography;

interface LinkedLineItemsModalProps {
	programId: string | null;
	programType: ProgramType;
	/** Every line item the program currently covers, oldest link first. */
	linkedLineItems: ProgramOrderLineItem[];
	open: boolean;
	onClose: () => void;
}

/**
 * Which copies of a part one program covers.
 *
 * The same part is often ordered several times on one order and programmed
 * once. Ticking a line item here says that program is the work for it too, so
 * it stops showing up in the grid as something still to be programmed.
 */
const LinkedLineItemsModal: React.FC<LinkedLineItemsModalProps> = ({
	programId,
	programType,
	linkedLineItems,
	open,
	onClose,
}) => {
	const [error, setError] = useState<string | null>(null);
	const [selected, setSelected] = useState<string[]>([]);
	const [setProgramLineItems, { isLoading: isSaving }] =
		useSetProgramLineItemsMutation();

	// The representative is what the candidate search is anchored to; every
	// other linked line item matches it by definition.
	const anchor = linkedLineItems[0] ?? null;
	const { data, isFetching } = useGetLinkableLineItemsQuery(
		{
			orderLineItemId: anchor?.id as string,
			program: programId,
			programType,
		},
		{ skip: !open || !programId || !anchor }
	);

	const options = useMemo(() => {
		const byId = new Map<string, ProgramOrderLineItem>();
		for (const lineItem of linkedLineItems) {
			byId.set(lineItem.id, lineItem);
		}
		for (const candidate of data?.results ?? []) {
			byId.set(candidate.id, candidate);
		}
		return [...byId.values()];
	}, [linkedLineItems, data]);

	useEffect(() => {
		if (open) {
			setError(null);
			setSelected(linkedLineItems.map((lineItem) => lineItem.id));
		}
	}, [open, linkedLineItems]);

	const handleSave = async () => {
		if (!programId) {
			return;
		}
		setError(null);
		try {
			await setProgramLineItems({
				program: programId,
				order_line_items: selected,
			}).unwrap();
			message.success('Linked parts updated');
			onClose();
		} catch (err) {
			setError(programApiErrorMessage(err, 'Failed to update linked parts'));
		}
	};

	return (
		<Modal
			className="cam-modal"
			title="Parts covered by this program"
			open={open}
			centered
			width={900}
			onCancel={onClose}
			onOk={handleSave}
			okText="Save"
			okButtonProps={{ disabled: selected.length === 0 }}
			confirmLoading={isSaving}
			destroyOnClose
		>
			{error && (
				<Alert
					type="error"
					showIcon
					message={error}
					style={{ marginBottom: 16 }}
				/>
			)}
			<Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
				{programLinkingExplanation(programType)}
			</Text>
			{isFetching && options.length === 0 ? (
				<Text type="secondary">Loading parts…</Text>
			) : options.length === 0 ? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description="No matching parts on this order"
				/>
			) : (
				<ProgramLinkingLineItemsTable
					lineItems={options}
					value={selected}
					onChange={setSelected}
				/>
			)}
			{selected.length === 0 && (
				<Text type="warning" style={{ display: 'block', marginTop: 12 }}>
					A program has to cover at least one part.
				</Text>
			)}
		</Modal>
	);
};

export default LinkedLineItemsModal;
